<script setup>
import { currentDayRange } from '../dateTime/historyDateTime.js'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ArrowPathIcon, CheckCircleIcon, FunnelIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerSelector from '../components/pts2/Pts2ControllerSelector.vue'
import Pts2DeliveryDrawer from '../components/pts2/Pts2DeliveryDrawer.vue'
import Pts2DeliveryFilters from '../components/pts2/Pts2DeliveryFilters.vue'
import Pts2EntityBadge from '../components/pts2/Pts2EntityBadge.vue'
import Pts2HistoryExport from '../components/pts2/Pts2HistoryExport.vue'
import Pts2FilterChip from '../components/pts2/Pts2FilterChip.vue'
import { useI18n } from '../i18n.js'
import { normalizePts2Context } from '../pts2/context.js'
import { deliveryRangeIssue, getPts2Deliveries, getPts2DeliveryDetail, getPts2DeliveryOptions, paginationItems } from '../pts2/deliveries.js'
import { choosePts2Controller, loadPts2HistoryContext, session } from '../session.js'
import { createFacetLifecycle, emptyFacetOptions, facetItemsWithSelection, retainFacetSelection } from '../pts2/filterOptions.js'
import { useHistorySession } from '../pts2/historySession.js'
import { historyScope } from '../pts2/historyState.js'
import '../pts2/controllers.css'

const context = computed(() => normalizePts2Context(session.pts2Context ?? {}))
const { locale, t } = useI18n()
const page = ref(emptyPage())
const pageNumber = ref(1)
const pageSize = ref(25)
const filtersOpen = ref(false)
const loading = ref(true)
const refreshing = ref(false)
const switching = ref(false)
const error = ref('')
const filterError = ref('')
const updatedAt = ref(null)
const options = reactive({ TANK: emptyOptions(), FUEL: emptyOptions() })
const applied = reactive(defaultFilters())
const draft = reactive(draftFrom(applied))
const selectedId = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')
let listRequest
let detailRequest
let generation = 0, ready = false, disposed = false, optionTimer
const denied = ref(false)
const scope = () => historyScope(session)

const cachedStale = ref(false)
const history = useHistorySession('deliveries', { applied, draft, pageNumber, pageSize, options, denied })
const unapplied = history.unapplied

const resultFrom = computed(() => page.value.totalItems ? (page.value.page - 1) * page.value.limit + 1 : 0)
const resultTo = computed(() => page.value.totalItems ? resultFrom.value + page.value.items.length - 1 : 0)
const pageButtons = computed(() => paginationItems(page.value.page, page.value.totalPages))
const activeFilterGroups = computed(() => Number(applied.tankNumbers.length > 0) + Number(applied.fuelGradeIds.length > 0)
  + Number(applied.completeness !== 'ALL'))
const hasConditions = computed(() => activeFilterGroups.value > 0)
const activeChips = computed(() => {
  const result = []
  for (const number of applied.tankNumbers) result.push({ key: `tank:${number}`,
    label: number === 0 ? t('pts2UiHistoryControllerTanks') : t('pts2UiHistoryTankNumber', { number }) })
  for (const id of applied.fuelGradeIds) {
    const option = facetItemsWithSelection(options.FUEL).find((item) => item.number === id)
    result.push({ key: `fuel:${id}`, entityId: id, appearance: 'entity', label: option?.name || t('pts2UiHistoryFuelId', { id }) })
  }
  if (applied.completeness === 'COMPLETE') result.push({ key: 'complete', label: t('pts2UiHistoryComplete') })
  if (applied.completeness === 'INCOMPLETE') result.push({ key: 'complete', label: t('pts2UiHistoryIncomplete') })
  return result
})
const stateLabels = computed(() => ({ NORMAL: t('pts2UiHistoryComplete'), INCOMPLETE: t('pts2UiHistoryIncomplete') }))
const stateIcons = { NORMAL: CheckCircleIcon, INCOMPLETE: InformationCircleIcon }

function emptyPage() {
  return { items: [], page: 1, limit: 25, totalItems: 0, totalPages: 0,
    summary: { all: 0, productVolume: null, productVolumeKnown: 0, pumpsDispensedVolume: null, pumpsDispensedKnown: 0, incomplete: 0 } }
}
function emptyOptions() { return emptyFacetOptions() }
function initialRange() {
  return currentDayRange()
}
function defaultFilters() { return { ...initialRange(), tankNumbers: [], fuelGradeIds: [], completeness: 'ALL' } }
function draftFrom(value) { return { ...value, tankNumbers: [...value.tankNumbers], fuelGradeIds: [...value.fuelGradeIds] } }
function criteria(value = applied) {
  return { stationId: context.value.stationId, controllerId: context.value.activeControllerId, ...value,
    tankNumbers: [...value.tankNumbers], fuelGradeIds: [...value.fuelGradeIds] }
}

const optionCriteria = () => criteria()
let observedZone = null, optionZoneGeneration = 0
const optionScope = () => JSON.stringify([scope(), optionZoneGeneration])
function observeOptionsZone(items) {
  const zone = items.find((item) => item.stationTimeZone)?.stationTimeZone
  if (!zone) return
  if (observedZone && observedZone !== zone) {
    optionZoneGeneration++; facets.sync()
    if (filtersOpen.value && ready && !denied.value) facets.ensure()
  }
  observedZone = zone
}
const facets = createFacetLifecycle({ states: options, getCriteria: optionCriteria, getScope: optionScope,
  isValid: (value) => ready && !denied.value && !switching.value && Number(value.stationId) === Number(session.platformContext?.activeStationId) && !deliveryRangeIssue(value.from, value.to), load: getPts2DeliveryOptions,
  onDenied: loseAccess,
})

function facetsValid(value) { return !deliveryRangeIssue(value.from, value.to) }
async function initialize() {
  ready = false; loading.value = true; error.value = ''
  try {
    const value = await loadPts2HistoryContext({ isCurrent: () => !disposed })
    if (!value || disposed) return
    if (!context.value.activeControllerId) { history.deny(); loading.value = false; return }
    const retained = history.restore()
    ready = true
    if (filtersOpen.value) facets.ensure()
    if (retained?.page) {
      page.value = retained.page; updatedAt.value = new Date(retained.fetchedAt).toISOString()
      cachedStale.value = !retained.fresh; loading.value = false
      if (!retained.fresh) await refresh()
    } else await refresh({ initial: true })
  } catch (cause) {
    if (disposed) return
    if ([401, 403, 404].includes(cause?.status)) loseAccess(cause)
    else { history.deny(); error.value = message(cause); loading.value = false }
  }
}

async function refresh({ resetPage = false, initial = false } = {}) {
  if (denied.value || !context.value.stationId || !context.value.activeControllerId) { page.value = emptyPage(); loading.value = false; return }
  if (!facetsValid(applied)) return
  if (resetPage) pageNumber.value = 1
  if (!history.matchesQuery()) { page.value = emptyPage(); cachedStale.value = false }
  const descriptor = history.query()
  listRequest?.abort(); listRequest = new AbortController()
  const request = listRequest, current = ++generation, requestScope = scope()
  if (initial || !page.value.items.length) loading.value = true
  refreshing.value = true; error.value = ''
  try {
    const currentCriteria = criteria()
    const result = await getPts2Deliveries(currentCriteria, { page: pageNumber.value, limit: pageSize.value, signal: request.signal })
    if (request.signal.aborted || current !== generation || requestScope !== scope() || disposed) return
    observeOptionsZone(result.items)
    page.value = result
    if (result.totalPages && result.page > result.totalPages) { pageNumber.value = result.totalPages; return refresh() }
    if (!result.totalItems && result.page !== 1) { pageNumber.value = 1; return refresh() }
    updatedAt.value = new Date().toISOString()
    pageNumber.value = result.page
    history.saveResult({ ...descriptor, pageNumber: result.page }, result); cachedStale.value = false
  } catch (cause) {
    if (cause?.name !== 'AbortError' && current === generation && requestScope === scope() && !disposed) {
      if ([401, 403, 404].includes(cause?.status)) { loseAccess(cause); return }
      else if (!page.value.items.length) page.value = emptyPage()
      error.value = message(cause)
    }
  } finally {
    if (current === generation) { loading.value = false; refreshing.value = false }
  }
}
function loadOption(facet, cursor, force = false) {
  if (!ready || denied.value || switching.value) return
  return facets.load(facet, cursor === undefined ? options[facet].requestedCursor : cursor, { force })
}
function reloadOptions() { if (ready && !denied.value) facets.refresh() }
async function selectController(id) {
  clearTimeout(optionTimer); clearOptions(); switching.value = true; error.value = ''; closeDrawer()
  try {
    const value = await choosePts2Controller(id)
    if (!value || disposed) return
    clearOptions(); Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
    await refresh({ resetPage: true, initial: true })
  } catch (cause) { error.value = message(cause) }
  finally { switching.value = false; if (filtersOpen.value && !denied.value) facets.ensure() }
}
async function applyFilters() {
  const issue = deliveryRangeIssue(draft.from, draft.to)
  if (issue === 'REQUIRED') { filterError.value = t('pts2UiHistoryRangeRequired'); return }
  if (issue === 'ORDER') { filterError.value = t('pts2UiHistoryRangeOrder'); return }
  if (issue === 'TOO_LONG') { filterError.value = t('pts2UiHistoryRangeTooLong'); return }
  Object.assign(applied, draftFrom(draft)); filtersOpen.value = false; filterError.value = ''; closeDrawer()
  await Promise.all([refresh({ resetPage: true }), facets.ensure()])
}
async function clearFilters() {
  Object.assign(draft, draftFrom(defaultFilters())); filterError.value = ''; filtersOpen.value = true
}
async function removeChip(key) {
  if (key.startsWith('tank:')) draft.tankNumbers = draft.tankNumbers.filter((id) => id !== Number(key.slice(5)))
  else if (key.startsWith('fuel:')) draft.fuelGradeIds = draft.fuelGradeIds.filter((id) => id !== Number(key.slice(5)))
  else if (key === 'complete') draft.completeness = 'ALL'
  filterError.value = ''; filtersOpen.value = true
}
async function goToPage(value) { pageNumber.value = value; closeDrawer(); await refresh() }
async function changePageSize(value) { pageSize.value = Number(value); closeDrawer(); await refresh({ resetPage: true }) }
async function openDelivery(item) { selectedId.value = item.id; await loadDetail(item.id) }
async function loadDetail(id = selectedId.value) {
  if (!id) return
  detail.value = null; detailError.value = ''; detailLoading.value = true
  detailRequest?.abort(); const request = new AbortController(); detailRequest = request
  const requestScope = scope()
  try {
    const value = await getPts2DeliveryDetail(context.value.stationId, context.value.activeControllerId, id, { signal: request.signal })
    if (!request.signal.aborted && detailRequest === request && selectedId.value === id && requestScope === scope() && !disposed) detail.value = value
  } catch (cause) {
    if (cause?.name !== 'AbortError' && detailRequest === request && selectedId.value === id && requestScope === scope() && !disposed) {
      if ([401, 403, 404].includes(cause?.status)) loseAccess(cause)
      else detailError.value = message(cause, true)
    }
  }
  finally { if (detailRequest === request && !request.signal.aborted) detailLoading.value = false }
}
function closeDrawer() { detailRequest?.abort(); selectedId.value = null; detail.value = null; detailError.value = ''; detailLoading.value = false }
function clearOptions() { clearTimeout(optionTimer); facets.clear() }
function loseAccess(cause) {
  history.deny(); cachedStale.value = false
  denied.value = true; generation++; listRequest?.abort(); clearOptions(); closeDrawer(); page.value = emptyPage()
  Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
  error.value = message(cause); loading.value = false; refreshing.value = false
}

function message(value, isDetail = false) {
  if ([401, 403, 404].includes(value?.status)) return t('pts2UiHistoryAccessDenied')
  return isDetail ? t('pts2UiHistoryLoadDeliveryDetailError') : t('pts2UiHistoryLoadDeliveriesError')
}
function date(value, timeZone) {
  if (!value) return '—'
  const parsed = new Date(value); if (!Number.isFinite(parsed.getTime())) return String(value)
  const format = { dateStyle: 'short', timeStyle: 'medium' }; if (timeZone) format.timeZone = timeZone
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(parsed)
}
function sourceTime(instant, local, quality, timeZone) {
  if (instant) return date(instant, timeZone)
  if (!local) return '—'
  const suffix = quality === 'AMBIGUOUS' ? t('pts2UiHistoryAmbiguousTime') : quality === 'INVALID' ? t('pts2UiHistoryInvalidTime') : ''
  return `${local.replace('T', ', ')}${suffix}`
}
function number(value, digits = 1) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : String(value)
}
function summaryVolume(value) { return value == null ? '—' : `${number(value, 1)} ${t('pts2UiHistoryLiters')}` }
function volume(value, digits = 0) { return value == null ? '—' : number(value, digits) }
function duration(value) {
  if (value == null) return '—'
  const hours = Math.floor(value / 3600), minutes = Math.round(value % 3600 / 60)
  return hours ? t('pts2UiHistoryHoursMinutes', { hours, minutes }) : t('pts2UiHistoryMinutes', { minutes })
}
function tank(value) { return value === 0 ? t('pts2UiHistoryAllTanks') : value == null ? '—' : `№${value}` }
function fuel(item) { return item.fuelGradeName || (item.fuelGradeId == null ? '—' : t('pts2UiHistoryFuelId', { id: item.fuelGradeId })) }

watch(scope, () => {
  history.resetOwner(); cachedStale.value = false; filtersOpen.value = false
  observedZone = null
  generation++; listRequest?.abort(); clearTimeout(optionTimer); clearOptions(); closeDrawer(); page.value = emptyPage()
  updatedAt.value = null; loading.value = false; refreshing.value = false; denied.value = false
  Object.assign(applied, defaultFilters()); Object.assign(draft, draftFrom(applied))
  if (ready && !switching.value && session.user && context.value.activeControllerId) { if (filtersOpen.value) facets.ensure(); refresh({ resetPage: true, initial: true }) }
}, { flush: 'sync' })
watch(optionCriteria, () => {
  clearTimeout(optionTimer)
  retainFacetSelection(options.TANK, draft.tankNumbers); retainFacetSelection(options.FUEL, draft.fuelGradeIds)
  facets.sync()
  if (ready && filtersOpen.value && !switching.value && !denied.value) optionTimer = setTimeout(() => facets.ensure(), 150)
}, { deep: true })
watch(draft, () => {
  filterError.value = ''
  retainFacetSelection(options.TANK, draft.tankNumbers); retainFacetSelection(options.FUEL, draft.fuelGradeIds)
}, { deep: true })
watch(filtersOpen, (open) => { if (open && ready && !switching.value && !denied.value) facets.ensure() })
onMounted(initialize)
onBeforeUnmount(() => { history.capture(); disposed = true; ready = false; generation++; clearTimeout(optionTimer); facets.dispose(); listRequest?.abort(); detailRequest?.abort() })
</script>

<template>
  <section class="pc-page pts2-transactions-page pts2-deliveries-page">
    <header class="pts2-transactions-heading"><div><h1>{{ t('pts2DeliveriesTitle') }}</h1><p>{{ t('pts2DeliveriesHint') }}</p></div>
      <span v-if="updatedAt">{{ t('pts2UiHistoryUpdated', { time: date(updatedAt).split(', ').at(-1) }) }}</span></header>
    <div class="pts2-transactions-context">
      <Pts2ControllerSelector :controllers="context.controllers" :active-controller-id="context.activeControllerId"
        :disabled="loading || switching" @select="selectController" />
      <button class="pc-secondary" type="button" :disabled="refreshing || switching || denied || !context.activeControllerId" @click="refresh(); reloadOptions()">
        <ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />{{ refreshing ? t('pts2UiHistoryRefreshing') : t('pts2UiHistoryRefresh') }}
      </button>
    </div>
    <p v-if="unapplied" class="notice" role="status">{{ t('pts2UiHistoryUnappliedChanges') }}</p>
    <p v-if="cachedStale" class="notice" role="status">{{ t('pts2UiHistoryRetainedStale') }}</p>
    <section v-if="!loading && !context.controllers.length" class="pts2-empty-state"><strong>{{ t('pts2UiNoStationControllers') }}</strong></section>
    <section v-else-if="!loading && context.selectionRequired && !context.activeControllerId" class="pts2-empty-state"><strong>{{ t('pts2UiChooseController') }}</strong><p>{{ t('pts2UiHistoryDeliveriesIsolation') }}</p></section>
    <section v-else class="pts2-transaction-workspace">
      <section class="pts2-delivery-summary-grid" :aria-label="t('pts2UiHistorySummary')">
        <article><span>{{ t('pts2UiHistoryDeliveryCount') }}</span><strong>{{ loading ? '…' : page.summary.all }}</strong><small>{{ t('pts2UiHistorySelectionRecords') }}</small></article>
        <article><span>{{ t('pts2UiHistoryPtsVolume') }}</span><strong>{{ loading ? '…' : summaryVolume(page.summary.productVolume) }}</strong><small>{{ loading ? t('pts2UiHistoryWaiting') : t('pts2UiHistoryRecordsOf', { count: page.summary.productVolumeKnown, total: page.summary.all }) }}</small></article>
        <article><span>{{ t('pts2UiHistoryPumpsDispensed') }}</span><strong>{{ loading ? '…' : summaryVolume(page.summary.pumpsDispensedVolume) }}</strong><small>{{ loading ? t('pts2UiHistoryWaiting') : t('pts2UiHistoryRecordsOf', { count: page.summary.pumpsDispensedKnown, total: page.summary.all }) }}</small></article>
        <article :class="{ attention: !loading && page.summary.incomplete }"><span>{{ t('pts2UiHistoryIncompleteRecords') }}</span><strong>{{ loading ? '…' : page.summary.incomplete }}</strong><small>{{ t('pts2UiHistoryCheckData') }}</small></article>
      </section>
      <div class="pts2-delivery-toolbar">
        <span>{{ t('pts2UiHistorySummaryHint') }}</span>
        <Pts2HistoryExport journal="in-tank-deliveries" :filters="criteria()" :scope="JSON.stringify([scope(), switching, denied])" :disabled="loading || refreshing || switching || denied || !context.activeControllerId">
          <button class="pts2-filter-toggle pc-secondary" type="button" :aria-expanded="filtersOpen" aria-controls="pts2-delivery-filter-panel" @click="filtersOpen = !filtersOpen">
            <FunnelIcon aria-hidden="true" />{{ t('pts2UiHistoryFilters') }} <span v-if="activeFilterGroups">{{ activeFilterGroups }}</span>
          </button>
        </Pts2HistoryExport>
      </div>
      <section v-if="filtersOpen" id="pts2-delivery-filter-panel">
        <header><h2>{{ t('pts2UiHistoryFilterDeliveries') }}</h2><button type="button" :aria-label="t('pts2UiHistoryCloseFilters')" @click="filtersOpen = false">×</button></header>
        <Pts2DeliveryFilters :model-value="draft" @update:model-value="Object.assign(draft, $event)" :options="options" :loading="switching || denied"
          :range-error="filterError" @apply="applyFilters" @clear="clearFilters" @close="filtersOpen = false" @options="loadOption" />
      </section>
      <div v-if="activeChips.length" class="pts2-active-filter-chips" aria-live="polite">
        <Pts2FilterChip v-for="chip in activeChips" :key="chip.key" :entity-id="chip.entityId" :appearance="chip.appearance || 'neutral'" :label="chip.label" @remove="removeChip(chip.key)" />
        <button type="button" @click="clearFilters">{{ t('pts2UiHistoryClear') }}</button>
      </div>
      <p v-if="error && page.items.length" class="notice error pts2-background-error" role="alert">{{ error }}</p>
      <section class="pts2-transaction-table-card pts2-delivery-table-card"><header><h2>{{ t('pts2UiHistoryDeliveries') }}</h2><span>{{ t('pts2UiHistoryShown', { count: page.items.length, total: page.totalItems }) }}</span></header>
        <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2UiHistoryLoadingDeliveries') }}</p>
        <div v-else-if="error && !page.items.length" class="pts2-empty-state" role="alert"><strong>{{ error }}</strong><button class="pc-secondary" type="button" @click="refresh()">{{ t('pts2UiHistoryRetry') }}</button></div>
        <div v-else-if="page.items.length" class="pts2-table-scroll"><table><thead><tr>
          <th>{{ t('pts2UiHistoryStatus') }}</th><th>{{ t('pts2UiHistoryDeliveryPeriod') }}</th><th>{{ t('pts2UiHistoryDuration') }}</th><th>{{ t('pts2UiHistoryTank') }}</th><th>{{ t('pts2UiHistoryFuel') }}</th><th>{{ t('pts2UiHistoryStartLiters') }}</th><th>{{ t('pts2UiHistoryEndLiters') }}</th><th>{{ t('pts2UiHistoryDeliveryLiters') }}</th><th>{{ t('pts2UiHistoryPumpsLiters') }}</th><th :aria-label="t('pts2UiHistoryDetails')"></th>
        </tr></thead><tbody><tr v-for="item in page.items" :key="item.id" :class="['delivery-' + item.displayState.toLowerCase(), { selected: selectedId === item.id }]" tabindex="0"
          @click="openDelivery(item)" @keydown.enter.prevent="openDelivery(item)" @keydown.space.prevent="openDelivery(item)">
          <td :data-label="t('pts2UiHistoryStatus')"><span class="pts2-delivery-state" :class="item.displayState.toLowerCase()"><component :is="stateIcons[item.displayState]" aria-hidden="true" />{{ stateLabels[item.displayState] }}</span></td>
          <td :data-label="t('pts2UiHistoryDeliveryPeriod')"><span class="pts2-delivery-period"><b>{{ sourceTime(item.startedAt, item.startTimeLocal, item.startTimeQuality, item.stationTimeZone) }}</b><small>{{ sourceTime(item.eventAt, item.eventTimeLocal, item.eventTimeQuality, item.stationTimeZone) }}</small></span></td>
          <td :data-label="t('pts2UiHistoryDuration')">{{ duration(item.durationSeconds) }}</td><td :data-label="t('pts2UiHistoryTank')">{{ tank(item.tankNumber) }}</td>
          <td :data-label="t('pts2UiHistoryFuel')"><Pts2EntityBadge :entity-id="item.fuelGradeId" :label="fuel(item)" /></td>
          <td :data-label="t('pts2UiHistoryStartLiters')">{{ volume(item.startProductVolume) }}</td><td :data-label="t('pts2UiHistoryEndLiters')">{{ volume(item.endProductVolume) }}</td>
          <td :data-label="t('pts2UiHistoryDeliveryLiters')"><strong>{{ volume(item.productVolume) }}</strong></td><td :data-label="t('pts2UiHistoryPumpsLiters')">{{ volume(item.pumpsDispensedVolume, 1) }}</td>
          <td><button class="pts2-row-detail" type="button" :aria-label="t('pts2UiHistoryOpenDelivery', { time: sourceTime(item.eventAt, item.eventTimeLocal, item.eventTimeQuality, item.stationTimeZone) })" @click.stop="openDelivery(item)">›</button></td>
        </tr></tbody></table></div>
        <div v-else class="pts2-empty-state"><strong>{{ hasConditions ? t('pts2UiHistoryDeliveriesFilteredEmpty') : t('pts2UiHistoryDeliveriesEmpty') }}</strong><button v-if="hasConditions" class="pc-secondary" type="button" @click="clearFilters">{{ t('pts2UiHistoryClearFilters') }}</button></div>
        <footer v-if="page.totalItems" class="pts2-transaction-pagination"><label>{{ t('pts2UiHistoryRowsPerPage') }} <select :value="pageSize" :disabled="refreshing" @change="changePageSize($event.target.value)"><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option></select></label>
          <span>{{ t('pts2UiHistoryPageRange', { from: resultFrom, to: resultTo, total: page.totalItems }) }}</span><nav :aria-label="t('pts2UiHistoryDeliveryPagination')"><button type="button" :aria-label="t('pts2UiHistoryPrevious')" :disabled="page.page <= 1 || refreshing" @click="goToPage(page.page - 1)">‹</button>
            <template v-for="button in pageButtons" :key="button"><span v-if="typeof button === 'string'">…</span><button v-else type="button" :class="{ active: button === page.page }" :aria-current="button === page.page ? 'page' : undefined" :disabled="refreshing" @click="goToPage(button)">{{ button }}</button></template>
            <button type="button" :aria-label="t('pts2UiHistoryNext')" :disabled="page.page >= page.totalPages || refreshing" @click="goToPage(page.page + 1)">›</button></nav>
        </footer>
      </section>
    </section>
    <Pts2DeliveryDrawer v-if="selectedId" :detail="detail" :loading="detailLoading" :error="detailError" @retry="loadDetail()" @close="closeDrawer" />
  </section>
</template>
